import React from 'react';
import classNames from 'classnames';
import defaultTo from 'lodash/defaultTo';
import capitalize from 'lodash/capitalize';
import { token } from '@atlaskit/tokens';
import styled from '@emotion/styled';
import get from 'lodash/get';
import { ProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { ColGroup, CustomDynamicResultTable, PageTabs, TableBody, TableHead } from '@css-xp/base';
import { PricingPlan } from '@css-xp/graphtypes/src/generated-types-new';
import { errorStateProp, emptyStateProp, FormatCurrency, NoData } from '@css-xp/ppc-common';
import { getChargeElement } from '@css-xp/ppc-utils';
export interface PricingTiersTableProps {
  pricingPlan: PricingPlan | null;
  product?: ProductCatalogueItem;
  version?: number;
}

interface TableProps {
  label: string;
  pricingPlan: PricingPlan | null;
  product?: ProductCatalogueItem;
  version?: number;
}

const StyledPricingTierTable = styled.div`
  .tier-details {
    margin-top: 0px;
    table {
      thead {
        border-bottom: 2px solid ${token('color.border')} !important;
      }
    }
  }
`;

const TiersTable = ({ pricingPlan, product, version }: TableProps) => {
  const isVersion2 = version === 2;
  const getHead = (product?: ProductCatalogueItem) => {
    const cells = [
      {
        name: `${capitalize(getChargeElement({ productId: product?.id }))} tier`,
        customClassNames: isVersion2 ? 'col-60' : 'col-25',
      },
      {
        name: 'Unit amount',
        customClassNames: isVersion2 ? 'col-20' : 'col-25',
      },
      {
        name: 'Flat amount',
        customClassNames: isVersion2 ? 'col-20' : 'col-25',
      },
    ];

    return cells;
  };

  const sortedTiers = React.useMemo(() => {
    return [...defaultTo(get(pricingPlan, 'tiers'), [])].sort(
      (a, b) => defaultTo(get(a, 'floor'), 0) - defaultTo(get(b, 'floor'), 0)
    );
  }, [pricingPlan]);

  const renderPricingTierTable = () => {
    return (
      <div className={classNames({ 'pricing-tiers-table': !isVersion2 })}>
        <strong>{!isVersion2 && defaultTo(get(pricingPlan, 'description'), 'Not available')}</strong>
        <CustomDynamicResultTable
          emptyStateProps={emptyStateProp}
          noResults={defaultTo(get(pricingPlan, 'tiers'), []).length <= 0}
          isLoading={false}
          error={''}
          testId={'pricing-tiers-table'}
          className="tier-details"
          errorStateProps={errorStateProp}
        >
          <ColGroup columns={getHead(product)} />
          <TableHead columns={getHead(product)} />
          <TableBody>
            {sortedTiers.map((tier: any, index: any) => (
              <tr key={index}>
                <td>
                  {tier?.floor !== null && tier?.floor >= 0 ? (
                    tier?.ceiling === null ? (
                      `${tier?.floor} +`
                    ) : (
                      `${tier?.floor} - ${tier?.ceiling}`
                    )
                  ) : (
                    <NoData testId="no-user-tier-available" />
                  )}
                </td>
                <td>
                  {Number.isFinite(get(tier, 'unitAmount')) ? (
                    <FormatCurrency
                      value={defaultTo(get(tier, 'unitAmount'), 0)}
                      enableCurrencyBasedDenomination
                      currency={defaultTo(get(pricingPlan, 'currency'), '')}
                      showCurrencyCode
                      showCommas
                    />
                  ) : (
                    <NoData />
                  )}
                </td>
                <td>
                  {Number.isFinite(get(tier, 'flatAmount')) ? (
                    <FormatCurrency
                      value={defaultTo(get(tier, 'flatAmount'), 0)}
                      currency={defaultTo(get(pricingPlan, 'currency'), '')}
                      enableCurrencyBasedDenomination
                      showCurrencyCode
                      showCommas
                    />
                  ) : (
                    <NoData />
                  )}
                </td>
              </tr>
            ))}
          </TableBody>
        </CustomDynamicResultTable>
      </div>
    );
  };

  return isVersion2 ? (
    <StyledPricingTierTable>{renderPricingTierTable()}</StyledPricingTierTable>
  ) : (
    renderPricingTierTable()
  );
};

export const PricingTiersTable = ({ pricingPlan, product, version }: PricingTiersTableProps) => {
  return (
    <div>
      <PageTabs showAllTab={false}>
        <TiersTable label="Pricing plans" pricingPlan={pricingPlan} product={product} version={version} />
      </PageTabs>
    </div>
  );
};
